import { BusinessAnalytics } from "@xepp/business-analytics-sdk";
import { getCurrentUserId } from "../../guards/common/authentification-functions";
import { withoutEmpty } from "./customFields";

/**
 * Transaction PIN events for the web app, mirroring the "Transaction PIN" category of the
 * shared analytics event sheet: setting a PIN for the first time and resetting it via OTP.
 *
 * The two flows and their steps are seeded by the backend migration
 * SeedProductAnalyticsCatalog (flows 9 and 10) and must stay in step with it — ingestion
 * accepts unknown codes over HTTP and then silently discards them — so the codes live
 * here and nowhere else.
 */
export const TRANSACTION_PIN_FLOWS = {
	set: "transaction_pin_set",
	reset: "transaction_pin_reset",
} as const;

export const TRANSACTION_PIN_EVENTS = {
	setSuccess: "set_transaction_pin_success",
	setFailed: "set_transaction_pin_failed",
	resetSuccess: "reset_transaction_pin_success",
	resetFailed: "reset_transaction_pin_failed",
} as const;

/** `user_id` is the signed-in user's `nameid` claim, left out when nobody is signed in. */
const withCurrentUser = () => withoutEmpty({ user_id: getCurrentUserId() });

/** The save-PIN request succeeded. */
export const trackSetTransactionPinSuccess = (): void => {
	BusinessAnalytics.track(TRANSACTION_PIN_EVENTS.setSuccess, TRANSACTION_PIN_FLOWS.set, withCurrentUser());
};

/** The save-PIN request failed; client-side checks before it (mismatch, weak PIN) are not reported. */
export const trackSetTransactionPinFailed = (errorReason: string): void => {
	BusinessAnalytics.track(TRANSACTION_PIN_EVENTS.setFailed, TRANSACTION_PIN_FLOWS.set, withoutEmpty({ error_reason: errorReason }));
};

/** The finalize-reset request (OTP + new PIN) succeeded. */
export const trackResetTransactionPinSuccess = (): void => {
	BusinessAnalytics.track(TRANSACTION_PIN_EVENTS.resetSuccess, TRANSACTION_PIN_FLOWS.reset, withCurrentUser());
};

/** The finalize-reset request failed (wrong or expired OTP, server error); sending the OTP is not part of this. */
export const trackResetTransactionPinFailed = (errorReason: string): void => {
	BusinessAnalytics.track(TRANSACTION_PIN_EVENTS.resetFailed, TRANSACTION_PIN_FLOWS.reset, withoutEmpty({ error_reason: errorReason }));
};
